import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions, OfferPageMultiRoomAutoselectionReason } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useContext, useEffect } from 'react'
import { getActivePackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import { isBedbankMultiRoomEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import useOfferPageDispatch from './useOfferPageDispatch'
import { EmptyObject } from 'lib/object/objectUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'

export default function useBedbankIndividualRoomAutoselection() {
  const offerPageState = useContext(OfferPageStateContext)
  const { checkInDate, checkOutDate, rooms, multiRoomMode, bedbankOffer } = offerPageState

  const dispatch = useOfferPageDispatch()
  const multiRoomEnabled = useAppSelector(appState => isBedbankMultiRoomEnabled(offerPageState, appState))
  const rates = useAppSelector(appState => appState.offer.bedbankOfferRates[bedbankOffer?.id!]) ?? EmptyObject
  const isMobile = useScreenSizeOnly('mobile')

  const isMultiRoomUIActive = multiRoomEnabled && rooms.length > 1 && multiRoomMode === 'individual' && isMobile

  const areAnyRatesLoading = !!(checkInDate && checkOutDate && rooms.some(room => !rates[getBedbankRateKey([room], checkInDate, checkOutDate)]))

  const selectedPackagesByRoom = useAppSelector(appState => getActivePackagesByRoom(offerPageState, appState))

  useEffect(() => {
    if (!isMultiRoomUIActive || areAnyRatesLoading) {
      return
    }

    const anySelectionsDiffer = rooms.some(room => {
      const selection = selectedPackagesByRoom[room.id]
      return room.roomId !== selection?.roomId || room.rateId !== selection?.rateId
    })

    if (!anySelectionsDiffer) {
      return
    }

    const allSelections = Object.values(selectedPackagesByRoom)
    let autoselectionReason: OfferPageMultiRoomAutoselectionReason | undefined = undefined
    if (allSelections.some(selection => selection?.selectionType === 'wasUnavailable')) {
      autoselectionReason = 'invalid-selections'
    } else {
      autoselectionReason = 'initial-selection'
    }

    const newRooms = rooms.map(room => {
      const selection = selectedPackagesByRoom[room.id]
      return {
        ...room,
        roomId: selection?.roomId,
        rateId: selection?.rateId,
      }
    })

    dispatch({ type: OfferPageActions.setRooms, rooms: newRooms })
    dispatch({ type: OfferPageActions.setMultiRoomAutoselectionReason, reason: autoselectionReason })
  }, [dispatch, isMultiRoomUIActive, rooms, selectedPackagesByRoom, areAnyRatesLoading])
}
